import React, { useState } from "react";
import { Download, X, Share } from "lucide-react";
import { useInstallPrompt } from "../hooks/useInstallPrompt";

/**
 * Botão "Instalar app". Renderiza nada se o app já está em standalone
 * ou se o navegador não suporta instalação (e não é iOS).
 *
 * Em Android/Chrome: dispara o prompt nativo.
 * Em iOS Safari: abre um mini-tooltip com instruções manuais.
 */
export default function InstallAppButton({ className = "", compact = false }) {
    const { canInstall, isIOS, promptInstall } = useInstallPrompt();
    const [iosOpen, setIosOpen] = useState(false);

    if (!canInstall && !isIOS) return null;

    if (isIOS) {
        return (
            <>
                <button
                    type="button"
                    onClick={() => setIosOpen(true)}
                    data-testid="install-app-btn"
                    className={className || "flex items-center gap-1.5 bg-primary hover:opacity-90 text-white rounded-full px-3 py-1.5 transition-opacity"}
                    aria-label="Instalar aplicativo"
                >
                    <Download className="w-3.5 h-3.5" strokeWidth={2.2} />
                    {!compact && <span className="text-xs font-semibold">Instalar app</span>}
                </button>

                {iosOpen && (
                    <div
                        className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4"
                        data-testid="ios-install-instructions"
                    >
                        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setIosOpen(false)} />
                        <div className="relative w-full max-w-sm bg-[#0d0d0d] border border-border rounded-lg p-5 sm:p-6">
                            <button
                                type="button"
                                onClick={() => setIosOpen(false)}
                                className="absolute top-3 right-3 p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-sm transition-colors"
                                aria-label="Fechar"
                            >
                                <X className="w-4 h-4" />
                            </button>
                            <h3 className="font-display text-lg font-bold tracking-tight mb-2">
                                Instalar no iPhone / iPad
                            </h3>
                            <ol className="space-y-3 text-sm text-foreground mt-4">
                                <li className="flex items-start gap-3">
                                    <span className="w-5 h-5 rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
                                    <span>
                                        Toque no botão <Share className="inline w-4 h-4 mx-0.5 -mt-0.5" strokeWidth={2.2} />
                                        <span className="font-semibold"> Compartilhar</span> do Safari.
                                    </span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="w-5 h-5 rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
                                    <span>Escolha <span className="font-semibold">"Adicionar à Tela de Início"</span>.</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="w-5 h-5 rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
                                    <span>Toque em <span className="font-semibold">Adicionar</span> no canto superior direito.</span>
                                </li>
                            </ol>
                        </div>
                    </div>
                )}
            </>
        );
    }

    return (
        <button
            type="button"
            onClick={promptInstall}
            data-testid="install-app-btn"
            className={className || "flex items-center gap-1.5 bg-primary hover:opacity-90 text-white rounded-full px-3 py-1.5 transition-opacity"}
            aria-label="Instalar aplicativo"
        >
            <Download className="w-3.5 h-3.5" strokeWidth={2.2} />
            {!compact && <span className="text-xs font-semibold">Instalar app</span>}
        </button>
    );
}
